import React, { useContext, useEffect, useRef, useState } from "react";
import { StyleSheet, View, Dimensions } from "react-native";
import { Modal, Text, TouchableOpacity } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import Spinner from "../../../components/shared/Spinner";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useToast } from "react-native-toast-notifications";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import {
  approveCancelledOrder,
  getAllCancelledOrders,
  getMergedTrackerOrders,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import {
  translateFields,
  translateHeaders,
  useTranslation,
} from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";
import {
  resetCancelledSearch,
  searchCancelledTrackerOrders,
} from "../../../redux/reducers/chinaTrackerReducer";
import { ChinaTrackerDataTableWithPagination2 } from "../../../components/custom/DataTable/tracker/ChinaTrackerDataTableWithPagination2";
import { MenuProvider } from "react-native-popup-menu";
import { useNavigation } from "@react-navigation/native";
import TrackerHistoryModal from "./TrackerHistoryModal";
import ChinaShipmentsListModal from "./ChinaShipmentsListModal";
import ChinaTrackerDetailsReportModal from "./ChinaTrackerDetailsReportModal";

// Header definitions based on the table in the image
export const orderTrackingHeaders = {
  MSRef: {
    name: "MS No.",
    width: 0.3,
  },
  OrderNumber1: {
    name: "Order Number",
    width: 0.3,
  },
  allocateProductionLine: {
    name: "Production Line",
    width: 0.25,
  },
  ItemCode: {
    name: "Item Code",
    width: 0.3,
  },
  ItemName: {
    name: "Item Name",
    width: 0.65,
  },
  StateDelivery: {
    name: "State",
    width: 0.2,
  },
  OrderDate: {
    name: "PO Order Date",
    width: 0.3,
  },
  PriorityOrder: {
    name: "Priority Order",
    width: 0.25,
  },
  ArtworkNo: {
    name: "Artwork No",
    width: 0.2,
  },
  RequestDate: {
    name: "Due Date",
    width: 0.25,
  },
  TotalOrdered: {
    name: "Order Qty (Uom)",
    width: 0.3,
    textAlign: "center",
  },
  QtyToShip: {
    name: "Qty to Ship",
    width: 0.25,
  },
  FulfillmentPercent: {
    name: "Fulfilments %",
    width: 0.3,
  },
  Key: {
    name: "Key",
    width: 0.2,
    textAlign: "left",
    display: "hidden",
  },
  OrderStatus: {
    name: "Order Status",
    width: 0.2,
    textAlign: "left",
  },
  archivedActions: {
    name: "",
    width: 0.2,
    textAlign: "center",
    justifyContent: "center",
  },
};

const ChinaTrackerCancelledOrders: React.FC = ({
  visible,
  onClose,
  order,
}: any) => {
  const listRef = useRef<ScrollView>(null);
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get("window").width,
  );
  const [screenHeight, setScreenHeight] = useState(
    Dimensions.get("window").height,
  );
  const [tableHeaders, setTableHeaders] =
    useState<Partial<typeof orderTrackingHeaders>>(orderTrackingHeaders);

  const { cancelledOrders, loading, loadingCancel } = useSelector(
    (state: any) => state.chinaTracker,
  );
  const { t } = useTranslation();

  const [isSearched, setIsSearched] = useState<boolean>(false);
  const dispatch = useDispatch<any>();
  const [actionRows, setActionRows] = useState<any>([]);

  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);

  const toast = useToast();
  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext,
  );
  const [, setContentOffSet] = useState<number>(0);

  const [modalOpen, setModalOpen] = useState<boolean>();
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedEnquiryType, setSelectedEnquiryType] = useState<any>(null);

  const [orders, setOrders] = useState<any[]>([]);

  const [statusFilter, setStatusFilter] = useState<string>("all");

  const applyStatusFilter = (data: any[]) => {
    if (statusFilter === "all") return data;

    return data.filter((o) => {
      return o.OrderStatus === statusFilter;
    });
  };

  const navigate = useNavigation();

  const navigateToOrder = (order: any, action: string) => {
    if (action === "accept") {
      const orders: any = [];
      for (const orderKey of order) {
        orders.push({
          key: orderKey.Key,
          orderNumber: orderKey.OrderNumber1,
          MSRef: orderKey.MSRef,
          itemName: orderKey.ItemName,
          itemCode: orderKey.ItemCode,
          orderDate: orderKey.OrderDate,
          requestDate: orderKey.RequestDate.trim(),
          state: orderKey.StateDelivery,
        });
      }
      const payload: any = {
        orders: orders,
      };
      dispatch(approveCancelledOrder(payload))
        .unwrap()
        .then(() => {
          toast.show("Incoming order approved successfully", {
            type: "success",
          });
          setActionRows([]);
          dispatch(getAllCancelledOrders()).unwrap();
        })
        .catch((error: any) => {
          toast.show(
            error || "Error occurred during approval of incoming order",
            {
              type: "danger",
            },
          );
        });
    } else {
      // onClose();
      setModalOpen(true);
      setSelectedItem(order);
      setSelectedEnquiryType(action);
    }
  };
  // Update dimensions when screen size changes
  useEffect(() => {
    const updateDimensions = () => {
      const { width, height } = Dimensions.get("window");
      setScreenWidth(width);
      setScreenHeight(height);
    };

    const dimensionsListener = Dimensions.addEventListener(
      "change",
      updateDimensions,
    );

    return () => {
      dimensionsListener.remove();
    };
  }, []);

  const handleScroll = (event: any) => {
    setContentOffSet(Math.floor(event.nativeEvent?.contentOffset?.y) ?? 0);
  };

  // Calculate modal width based on screen size
  const getModalWidth = () => {
    if (isSmallDevice) return "100%";
    if (isMediumDevice) return "94%";
    return "92%";
  };

  // Calculate inner container width based on screen size
  const getInnerContainerWidth = () => {
    if (isSmallDevice) return "100%";
    return "100%";
  };

  // Hook form setup
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handleSearch = (searchedText: string) => {
    setPage(0);
    if (searchedText) {
      dispatch(searchCancelledTrackerOrders(searchedText));
    } else {
      dispatch(resetCancelledSearch());
    }
  };

  useEffect(() => {
    let isMounted: boolean = true;
    if (isRefresh) {
      dispatch(getMergedTrackerOrders({ refresh: true, showLoading: false }));
      setRefresh(false);
    }

    return () => {
      isMounted = false;
    };
  }, [isRefresh, isLargeDevice, isMediumDevice, isSmallDevice, screenWidth]);

  useEffect(() => {
    setRefresh(false);

    let filtered = applyStatusFilter(cancelledOrders);
    setOrders(filtered);
    let headers;
    headers = translateHeaders(
      orderTrackingHeaders,
      t,
      chinaTrackerTranslationMap,
    );

    const { Status, ...filteredHeaders } = headers;
    setTableHeaders(filteredHeaders);
  }, [cancelledOrders, statusFilter]);

  return (
    <>
      {modalOpen && selectedEnquiryType === "history" && (
        <TrackerHistoryModal
          onClose={() => setModalOpen(false)}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "shipments" && (
        <ChinaShipmentsListModal
          onClose={() => setModalOpen(false)}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "view" && (
        <ChinaTrackerDetailsReportModal
          onClose={() => setModalOpen(false)}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      <Modal visible={visible} transparent animationType="slide">
        <MenuProvider>
          <View style={styles.overlay}>
            {isLargeDevice ? (
              <View style={[styles.modalContainer, { width: getModalWidth() }]}>
                <View
                  style={[
                    styles.modalContainerSecond,
                    { width: getInnerContainerWidth(), height: "95%" },
                    isSmallDevice && styles.modalContainerMobile,
                  ]}
                >
                  <View
                    style={[
                      styles.titleContainer,
                      isSmallDevice && styles.titleContainerMobile,
                    ]}
                  >
                    <Text
                      style={[
                        styles.titleText,
                        isSmallDevice && styles.titleTextSmall,
                      ]}
                    >
                      {t("archievedOrders")}
                    </Text>
                    {!isSmallDevice && (
                      <View style={[styles.searchContainer]}>
                        <LabelBannerWithSearchBar
                          titleName={``}
                          handleSearch={handleSearch}
                          setRefresh={setRefresh}
                          dataToExport={orders}
                          fileName="ItemTrackerScreen"
                          sheetName="Item Tracker Report Screen"
                        />
                      </View>
                    )}
                  </View>

                  {isSmallDevice && (
                    <View
                      style={[
                        styles.searchContainerMobile,
                        { marginBottom: 10 },
                      ]}
                    >
                      <LabelBannerWithSearchBar
                        titleName={``}
                        handleSearch={handleSearch}
                        setRefresh={setRefresh}
                        dataToExport={orders}
                        fileName="ItemTrackerScreen"
                        sheetName="Item Tracker Report Screen"
                      />
                    </View>
                  )}

                  <View
                    style={[
                      { marginLeft: 15, marginTop: 10, marginBottom: 10 },
                    ]}
                  >
                    <ScrollView horizontal>
                      <TouchableOpacity onPress={() => setStatusFilter("all")}>
                        <Text
                          style={
                            statusFilter === "all"
                              ? styles.selectedFilter
                              : styles.filterLabel
                          }
                        >
                          {t("all")}
                        </Text>
                      </TouchableOpacity>

                      <TouchableOpacity
                        onPress={() => setStatusFilter("Cancelled")}
                      >
                        <Text
                          style={
                            statusFilter === "Cancelled"
                              ? styles.selectedFilter
                              : styles.filterLabel
                          }
                        >
                          {t("cancelled")}
                        </Text>
                      </TouchableOpacity>

                      <TouchableOpacity
                        onPress={() => setStatusFilter("Closed")}
                      >
                        <Text
                          style={
                            statusFilter === "Closed"
                              ? styles.selectedFilter
                              : styles.filterLabel
                          }
                        >
                          {t("closed")}
                        </Text>
                      </TouchableOpacity>
                    </ScrollView>
                  </View>

                  <ScrollView onScroll={handleScroll} ref={listRef}>
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator
                      contentContainerStyle={{
                        minWidth: Math.max(screenWidth, 1200),
                      }}
                    >
                      <View
                        style={[
                          styles.containerContent,
                          { minWidth: Math.max(screenWidth, 1200) },
                        ]}
                      >
                        {loading || loadingCancel ? (
                          <Spinner
                            size="small"
                            color="#74BBFF"
                            styles={{ flexDirection: "row", margin: "20%" }}
                          />
                        ) : (
                          <ChinaTrackerDataTableWithPagination2
                            headers={tableHeaders}
                            rows={orders}
                            itemsPerPage={10}
                            isDataLoading={isSearched}
                            isDisplayTimeWithDate={true}
                            useModal={true}
                            isDetail={false}
                            isEdit={false}
                            isDelete={false}
                            actionRows={actionRows}
                            setActionRows={setActionRows}
                            itemTracker
                            setModalVisible={() => {}}
                            handleRowClick={() => {}}
                            setSelected={() => {}}
                            handleDeleteRow={() => {}}
                            t={t}
                            page={page}
                            setPage={setPage}
                            operationActions={navigateToOrder}
                            selectable={true}
                          />
                        )}
                      </View>
                    </ScrollView>
                  </ScrollView>
                  <View
                    style={[styles.footer, isSmallDevice && styles.footerSmall]}
                  >
                    <TouchableOpacity
                      onPress={onClose}
                      style={[
                        styles.closeButton,
                        isSmallDevice && styles.buttonSmall,
                      ]}
                    >
                      <Text style={isSmallDevice && styles.buttonTextSmall}>
                        {t("close")}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            ) : (
              <View
                style={[
                  styles.modalContainerSecond,
                  { width: "100%", height: "95%" },
                  isSmallDevice && styles.modalContainerMobile,
                ]}
              >
                <View
                  style={[
                    styles.titleContainer,
                    isSmallDevice && styles.titleContainerMobile,
                  ]}
                >
                  <Text
                    style={[
                      styles.titleText,
                      isSmallDevice && styles.titleTextSmall,
                    ]}
                  >
                    {t("cancelledOrders")}
                  </Text>
                  {!isSmallDevice && (
                    <View style={[styles.searchContainer]}>
                      <LabelBannerWithSearchBar
                        titleName={``}
                        handleSearch={handleSearch}
                        setRefresh={setRefresh}
                        dataToExport={orders}
                        fileName="ItemTrackerScreen"
                        sheetName="Item Tracker Report Screen"
                      />
                    </View>
                  )}
                </View>

                {isSmallDevice && (
                  <View
                    style={[styles.searchContainerMobile, { marginBottom: 10 }]}
                  >
                    <LabelBannerWithSearchBar
                      titleName={``}
                      handleSearch={handleSearch}
                      setRefresh={setRefresh}
                      dataToExport={orders}
                      fileName="ItemTrackerScreen"
                      sheetName="Item Tracker Report Screen"
                    />
                  </View>
                )}

                <View
                  style={[
                    styles.actionButtonsContainer,
                    { marginLeft: 15, marginTop: 10, marginBottom: 10 },
                  ]}
                >
                  <TouchableOpacity
                    style={
                      actionRows.length > 0
                        ? styles.actionButton
                        : styles.disabledActionButton
                    }
                    onPress={() => {
                      activeTab === "incoming"
                        ? navigateToOrder(actionRows, "accept")
                        : navigateToOrder(actionRows, "update_accept");
                    }}
                    disabled={actionRows.length > 0 ? false : true}
                  >
                    <Text style={styles.actionButtonText}>
                      {translateFields("Accept", t, chinaTrackerTranslationMap)}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={
                      actionRows.length > 0
                        ? styles.actionButton
                        : styles.disabledActionButton
                    }
                    disabled={actionRows.length > 0 ? false : true}
                    onPress={() => {
                      activeTab === "incoming"
                        ? navigateToOrder(actionRows, "reject")
                        : navigateToOrder(actionRows, "update_reject");
                    }}
                  >
                    <Text style={styles.actionButtonText}>
                      {translateFields("Reject", t, chinaTrackerTranslationMap)}
                    </Text>
                  </TouchableOpacity>
                </View>

                <ScrollView
                  onScroll={handleScroll}
                  ref={listRef}
                  style={isSmallDevice && styles.scrollBodySectionMobile}
                >
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator
                    contentContainerStyle={{
                      minWidth: Math.max(screenWidth, 1200),
                    }}
                  >
                    <View
                      style={[
                        styles.containerContent,
                        { minWidth: Math.max(screenWidth, 1200) },
                      ]}
                    >
                      {loading || loadingCancel ? (
                        <Spinner
                          size="small"
                          color="#74BBFF"
                          styles={{ flexDirection: "row", margin: "20%" }}
                        />
                      ) : (
                        <ChinaTrackerDataTableWithPagination2
                          headers={tableHeaders}
                          rows={orders}
                          itemsPerPage={10}
                          isDataLoading={isSearched}
                          isDisplayTimeWithDate={true}
                          useModal={true}
                          isDetail={false}
                          isEdit={false}
                          isDelete={false}
                          actionRows={actionRows}
                          setActionRows={setActionRows}
                          itemTracker
                          setModalVisible={() => {}}
                          handleRowClick={() => {}}
                          setSelected={() => {}}
                          handleDeleteRow={() => {}}
                          t={t}
                          page={page}
                          setPage={setPage}
                          operationActions={navigateToOrder}
                          selectable={true}
                        />
                      )}
                    </View>
                  </ScrollView>
                </ScrollView>
                <View
                  style={[styles.footer, isSmallDevice && styles.footerSmall]}
                >
                  <TouchableOpacity
                    onPress={onClose}
                    style={[
                      styles.closeButton,
                      isSmallDevice && styles.buttonSmall,
                    ]}
                  >
                    <Text style={isSmallDevice && styles.buttonTextSmall}>
                      Close
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>
        </MenuProvider>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.15)",
    paddingVertical: 16,
    paddingHorizontal: 8,
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: 20,
    elevation: 12,
    borderRadius: 12,
    width: "100%",
    maxHeight: "95%",
  },
  modalContainerSecond: {
    backgroundColor: "#fff",
    elevation: 12,
    borderRadius: 12,
    padding: 16,
    width: "100%",
    height: "95%",
  },
  titleContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    paddingVertical: 12,
    marginBottom: 12,
  },
  titleText: {
    fontSize: 18,
    fontWeight: "700",
  },
  titleTextSmall: {
    fontSize: 14,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  searchInput: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 4,
    padding: 8,
    width: 200,
  },
  searchButton: {
    backgroundColor: "#f0f0f0",
    padding: 8,
    borderRadius: 4,
    marginLeft: 5,
  },
  tabsContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    marginBottom: 8,
  },
  tab: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  activeTab: {
    borderBottomColor: "#3498db",
  },
  tabText: {
    color: "#999",
  },
  activeTabText: {
    color: "#3498db",
    fontWeight: "600",
  },
  actionButtonsContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 12,
    gap: 10,
  },
  actionButton: {
    backgroundColor: "#56B7FD",
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
    width: 67,
  },
  disabledActionButton: {
    backgroundColor: "#D8D8D8",
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
    width: 67,
  },
  actionButtonText: {
    fontSize: 10,
    color: "#fff",
    fontWeight: 700,
  },
  formSection: {
    marginTop: 20,
    paddingHorizontal: 10,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: 20,
    padding: 25,
  },
  footerSmall: {
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  closeButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    width: 120,
    alignItems: "center",
  },
  buttonSmall: {
    padding: 8,
    paddingHorizontal: 12,
  },
  buttonTextSmall: {
    fontSize: 12,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    width: "100%",
    paddingHorizontal: 0,
    paddingBottom: 0,
  },

  tableScrollWrapper: {
    flex: 1,
    maxHeight: 520,
    width: "100%",
  },

  filterLabel: {
    fontSize: 12,
    fontWeight: "500",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginHorizontal: 4,
    backgroundColor: "#F1F5F9",
    color: "#4B5563",
    overflow: "hidden",
  },

  selectedFilter: {
    fontSize: 12,
    fontWeight: "600",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginHorizontal: 4,
    backgroundColor: "#56B7FD",
    color: "#fff",
    overflow: "hidden",
  },
  modalContainerMobile: {
    padding: 10,
    borderRadius: 10,
  },
  titleContainerMobile: {
    flexDirection: "column",
    alignItems: "flex-start",
    paddingVertical: 8,
    marginBottom: 4,
    gap: 6,
  },
  searchContainerMobile: {
    width: "100%",
  },
  scrollBodySectionMobile: {
    paddingTop: 4,
  },
});

export default ChinaTrackerCancelledOrders;
